import { t } from '@lingui/macro';
import { ModifiersPayload } from 'controllers/SearchController/types';
import { FiltersParams } from 'types/params';

export type ModifiersWithTotal = [ModifiersPayload, number];

export enum ContentSuggestionParams {
    suggestedlabels = 'suggestedlabels',
    suggestedpacks = 'suggestedpacks',
}

export type ContentSuggestionValue = {
    slug: string | undefined;
    name: string | undefined;
    artworkUrl: string | undefined;
};

export type ContentSuggestionPayload = [
    ContentSuggestionParams,
    ContentSuggestionValue,
];

export type FilterSuggestion = [
    ModifiersPayload,
    Exclude<SuggestionsType, SuggestionsType.Label | SuggestionsType.Pack>,
];

export type ContentSuggestion = [
    ContentSuggestionPayload,
    SuggestionsType.Label | SuggestionsType.Pack | SuggestionsType.Recent,
];

export type Suggestions = FilterSuggestion | ContentSuggestion;

export enum SuggestionsType {
    Trending = 'trending',
    Filters = 'filters',
    Recent = 'recent',
    Label = 'label',
    Pack = 'pack',
}

export function isContentSuggestionParams(
    params: FiltersParams | ContentSuggestionParams,
): params is ContentSuggestionParams {
    return Object.values(ContentSuggestionParams).includes(
        params as ContentSuggestionParams,
    );
}

export function isContentSuggestionValue(
    value: string | ContentSuggestionValue | null,
): value is ContentSuggestionValue {
    return (value as ContentSuggestionValue).slug !== undefined;
}

export function isContentSuggestionPayload(
    modifier: ModifiersPayload | ContentSuggestionPayload,
): modifier is ContentSuggestionPayload {
    const [params] = modifier;

    return isContentSuggestionParams(params);
}

export function isFiltersParams(
    params: FiltersParams | ContentSuggestionParams,
): params is FiltersParams {
    return Object.values(FiltersParams).includes(params as FiltersParams);
}

export function isModifiersPayload(
    modifier: ModifiersPayload | ContentSuggestionPayload,
): modifier is ModifiersPayload {
    const [params] = modifier;

    return isFiltersParams(params);
}

export function isFilterSuggestion(
    suggestion: Suggestions,
): suggestion is FilterSuggestion {
    const [, suggestionType] = suggestion;

    return suggestionType === SuggestionsType.Filters;
}

export function isContentSuggestion(
    suggestion: Suggestions,
): suggestion is ContentSuggestion {
    const [[param], suggestionType] = suggestion;

    const isContentSuggestionParams =
        param === ContentSuggestionParams.suggestedlabels ||
        param === ContentSuggestionParams.suggestedpacks;

    return (
        suggestionType === SuggestionsType.Label ||
        suggestionType === SuggestionsType.Pack ||
        (isContentSuggestionParams && suggestionType === SuggestionsType.Recent)
    );
}

export const getSuggestionDisplayType: () => {
    [key: string]: string;
} = () => ({
    [FiltersParams.instruments]: t`Instruments`,
    [FiltersParams.genres]: t`Genres`,
    [FiltersParams.type]: t`Types`,
    [FiltersParams.sfxs]: t`SFX`,
    [FiltersParams.tags]: t`Tags`,
    [ContentSuggestionParams.suggestedlabels]: t`Label`,
    [ContentSuggestionParams.suggestedpacks]: t`Pack`,
    [FiltersParams.textsearch]: '', // no categories for text search
});

export type SearchBarContextType = {
    clearCtaLabel: string;
    hasFilterApplied: boolean;
    hasSuggestions: boolean;
    hasTextInputValue: boolean;
    inputRef: React.MutableRefObject<HTMLInputElement | null>;
    inputValue: string;
    isModal: boolean;
    isSuggestionsOpen: boolean;
    isSearchBarDisabled: boolean;
    isTrendingVisible: boolean;
    placeholder: string;
    predictiveInput: string;
    selectedSuggestionIndex: number;
    suggestions: Suggestions[];
    suggestionsFromFilters: FilterSuggestion[];
    applySuggestion: (
        payload?: ModifiersPayload | ContentSuggestionPayload,
    ) => void;
    closeSuggestions: () => void;
    handleClearCta: () => void | undefined;
    handleFocus: () => void | undefined;
    onInputChange: (value: string) => void | undefined;
    removeFilter: (modifiers: ModifiersPayload[]) => void;
    handleApplyTextSearch: () => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    handleKeyPress: (
        event: React.KeyboardEvent<HTMLInputElement>,
    ) => void | undefined;
};
